import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import clsx from "clsx";

// Types
import { INotification } from "@/Common/api/types/notifications";
// Material
import { Box, IconButton, ListItem, ListItemText } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
// Components
import DateDiff from "@Layouts/components/DateDiff";
import { NotificationSpan } from "@/Layouts/views/Header/components/Notifications";
// Hooks
import { useNotificationMutations } from "@/Common/hooks/Notifications";
import { useMutation, useMutationState } from "@tanstack/react-query";

interface INotificationItemProps {
	item: INotification;
	selected: string;
	setSelected: Dispatch<SetStateAction<string>>;
}

export const NotificationListItem = ({ item, selected, setSelected }: Readonly<INotificationItemProps>) => {
	const { updateReadNotification } = useNotificationMutations();
	const [optimisticRead, setOptimisticRead] = useState(item.read);

	useEffect(() => {
		setOptimisticRead(item.read);
	}, [setOptimisticRead, item]);

	const isMutatingANotification = useMutationState<boolean>({
		filters: { mutationKey: ["toggleNotificationRead"], status: "pending", exact: true },
		select: ({ state }) => state.status === "pending",
	});

	const { mutate: toggleNotificationRead, isPending } = useMutation({
		mutationKey: ["toggleNotificationRead"],
		mutationFn: () => updateReadNotification(item._id, !item.read, item.archived),
		onMutate() {
			setOptimisticRead(!item.read);
		},
		onSuccess() {
			setOptimisticRead(!item.read);
		},
		onError() {
			setOptimisticRead(item.read);
		},
	});

	const handleToggleNotificationRead = useCallback(() => {
		if (!isMutatingANotification.some(Boolean) && !isPending) {
			toggleNotificationRead();
		}
	}, [!isMutatingANotification.some(Boolean) && !isPending, toggleNotificationRead]);

	return (
		<ListItem
			className={clsx("min-h-[76px] !py-0 !pl-3 !pr-12 cursor-pointer group hover:bg-black/5 relative", {
				"h-14": selected !== item._id,
				"h-[98.5%]": selected === item._id,
			})}
			onClick={() => {
				if (selected !== item._id) {
					setSelected(item._id);
				} else {
					setSelected("");
				}
			}}
		>
			<ListItemText
				disableTypography
				className="!my-[10px]"
				secondaryTypographyProps={{ className: "mb-[3px] " }}
				secondary={
					<DateDiff
						classNameTitle={"!text-default"}
						classNameClock={"!text-default"}
						date={item.createdAt}
						noTooltip
						maxWidth="100%"
					/>
				}
				primary={<NotificationSpan str={item.message} clamped={selected !== item._id} />}
			/>
			<Box className="absolute right-1" justifyContent="center" alignContent="center">
				<IconButton
					className={clsx("absolute group-hover:!inline-flex hover:!bg-transparent !p-2 !mr-0", {
						hidden: optimisticRead,
					})}
					edge="end"
					aria-label="Change-state"
					title={item.read ? "Mark as unread" : "Mark as read"}
					onClick={(e) => {
						e.stopPropagation();
						handleToggleNotificationRead();
					}}
				>
					<CircleIcon
						className={clsx("!w-[11px] !h-[11px]", {
							"text-disabled": optimisticRead,
							"text-primary": !optimisticRead,
						})}
					/>
				</IconButton>
			</Box>
		</ListItem>
	);
};
